import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonKeys } from 'components/eventView/pages/eventData';
import { motion } from 'framer-motion';
import { AnimTap2, EFonts } from 'libs/styles/styling';
import { useAnalytics } from 'libs/utils/site/analytics';
import { useContext } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { CtxEventView } from 'session/ctxEventView';
import styled, { css } from 'styled-components/macro';

interface Props {
    key: ButtonKeys;
    iconName: IconName;
    text: string;
    linkTo: string;
    pcAnchor: string | null;
    externalLink?: string | null;
    forceHighlight?: boolean;
    className?: string;
    scrollContentRef?: React.RefObject<HTMLDivElement>;
    matchChildrenPath?: boolean;
    additionalMatchPaths?: string[];
}

export const NavigationButton = (props: Props) => {
    const ctxEventView = useContext(CtxEventView);
    const navigate = useNavigate();
    const location = useLocation();
    const analytics = useAnalytics();

    const isMobile = !ctxEventView.isWide;

    const isMatched = !!matchPath(
        {
            path: props.linkTo + (props.matchChildrenPath ? '/*' : '')
        },
        location.pathname
    );

    const isAdditionalPathMatched = (props.additionalMatchPaths ?? []).some(
        (pathToMatch) => !!matchPath({ path: pathToMatch }, location.pathname)
    );

    const isHighlighted =
        (isMobile && (isMatched || isAdditionalPathMatched)) || props.forceHighlight;
    const iconPrefix = isHighlighted ? 'fas' : 'far';

    function handleClickMobile() {
        navigate(props.linkTo);
        ctxEventView.setScrollToTop(true);
    }

    function handleClick() {
        analytics.track('event_ui.navigate.clicked', {
            source: 'menu',
            destination: props.key
        });

        if (props.externalLink) {
            window.open(props.externalLink, '_blank');
            return;
        }
        handleClickMobile();
    }

    return (
        <S_NavigationButton
            className={props.className}
            onClick={handleClick}
            $isMobile={isMobile}
            $isHighlighted={isHighlighted}
        >
            <S_Inner {...AnimTap2}>
                {iconPrefix && props.iconName && (
                    <S_Icon
                        key={iconPrefix + props.iconName}
                        icon={[iconPrefix, props.iconName]}
                        $isMobile={isMobile}
                    />
                )}
                <S_Text>{props.text}</S_Text>
            </S_Inner>
        </S_NavigationButton>
    );
};

const S_NavigationButton = styled(motion.div)<{ $isMobile: boolean; $isHighlighted: boolean }>`
    display: flex;

    ${(props) => {
        if (props.$isMobile) {
            return css`
                flex-direction: column;
                justify-content: center;
                align-items: center;
                padding: 0 10px;
                &:first-child {
                    border-top-left-radius: 50px;
                    border-bottom-left-radius: 50px;
                }
                &:last-child {
                    border-top-right-radius: 50px;
                    border-bottom-right-radius: 50px;
                }
            `;
        } else {
            return css`
                flex-direction: 'row';
                justify-content: start;
                padding: 17px 25px;
                align-items: center;
                background: ${() => (props.$isHighlighted ? '#f1f2f4' : '#ffffff')};
                &:first-child {
                    border-radius: 15px 15px 0 0;
                }
                &:last-child {
                    border-radius: 0 0 15px 15px;
                }
                &:first-child:last-child {
                    border-radius: 15px;
                }
            `;
        }
    }}
    &:hover {
        background-color: #f1f2f4;
        cursor: pointer;
    }
`;

const S_Inner = styled(motion.div)`
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    flex-direction: column;
    justify-content: center;
`;

const S_Icon = styled(FontAwesomeIcon)<{ $isMobile: boolean }>`
    font-size: 18px;
    width: 18px;
    color: #3a3a3a;
    margin: ${(props) => (props.$isMobile ? '0 0 11px 0' : '0 20px 0 0 ')};
`;

const S_Text = styled.div`
    font-size: 17px;
    color: #3a3a3a;
    font-family: ${EFonts.InterMedium};
    font-size: 12px;
    font-style: normal;
    white-space: nowrap;
`;
